import { useAppSelector } from "hooks/redux";
import { groupBy } from "lodash";
import React, { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { ITeeTimeNew } from "redux/reducers/models/teetime";
import "./playerBookingGraph.scss";

interface IProps {
  teetimes: Array<ITeeTimeNew>;
  courseIds: Array<number>;
}

const SINGLE_BAR_HEIGHT_BELOW_100 = 5;
const SINGLE_BAR_HEIGHT_ABOVE_100 = 1;

const DASHBOARD_COLOURS = [
  "#3D5FA0",
  "#1BAA5A",
  "#7357D6",
  "#F73952",
  "#C6B7F7",
  "#B846C3",
  "#E9ACE8",
  "#1B94FD",
  "#88C6F7",
  "#2CDFFD",
  "#5BCEEC",
  "#87CE9B",
  "#FEAB2D",
  "#FAC35B",
  "#FE2889",
  "#F7A8C9",
  "#F6A7A7",
];

export default function PlayerBookingGraph(props: IProps) {
  const { teetimes, courseIds } = props;
  const { t } = useTranslation();
  const { facilityStore } = useAppSelector(store => store);

  const [maxBookings, setMaxBookings] = useState(0);
  const [plotAreaHeight, setPlotAreaHeight] = useState(0);
  const [singleBarHeight, setSingleBarHeight] = useState(SINGLE_BAR_HEIGHT_BELOW_100);

  const [graphTeeTimeHours, setGraphTeeTimeHours] = useState<
    {
      hour: string;
      bookingsPerCourse: { courseId: number; bookings: number }[];
    }[]
  >([]);

  useEffect(() => {
    if (teetimes?.length > 0) {
      setGraphTeeTimeHoursFromTeeTimes();
    } else {
      const emptyGraph = generateDefaultGraph();
      setGraphTeeTimeHours(emptyGraph);
    }
  }, [teetimes]);

  //Set y-axis scale based on maximum bookings
  useEffect(() => {
    const scaleStep = getYAxisScale(maxBookings);
    const yAxisSteps = Math.ceil(maxBookings / scaleStep) + 1;
    const barHeight = maxBookings >= 100 ? SINGLE_BAR_HEIGHT_ABOVE_100 : SINGLE_BAR_HEIGHT_BELOW_100;
    const plotHeight = (yAxisSteps + 1) * scaleStep * barHeight;
    ReactDOM.unstable_batchedUpdates(() => {
      setPlotAreaHeight(plotHeight);
      setSingleBarHeight(barHeight);
    });
  }, [maxBookings]);

  function generateDefaultGraph() {
    setMaxBookings(25);
    return Array.from({ length: 14 }, (_, i) => {
      const hour = 6 + i;
      return {
        hour: `${hour} - ${hour + 1}`,
        bookingsPerCourse: Array.from({ length: 4 }, () => ({
          bookings: 0,
          courseId: null,
        })),
      };
    });
  }

  function setGraphTeeTimeHoursFromTeeTimes() {
    // Group tee times by hour block (e.g. "6-7", "7-8")
    const groupedTeeTimeHours = groupBy(teetimes, teeTime => {
      const splitStartTime = teeTime.start_time?.split(":");
      const hour = Number(splitStartTime?.[0]);

      return `${hour} - ${hour + 1}`;
    });

    // Helper to count booked slots within a list of tee times
    const result = Object.entries(groupedTeeTimeHours).map(([hourLabel, teeTimes]) => {
      const bookingsPerCourse = courseIds.map(courseId => {
        const bookings = teeTimes.reduce((acc, teeTime) => {
          const count =
            teeTime.slots?.filter(slot => slot?.course_id === courseId && slot?.tee_time_slot_status === "booked")
              .length || 0;
          return acc + count;
        }, 0);

        return { courseId, bookings };
      });

      return {
        hour: hourLabel,
        bookingsPerCourse,
      };
    });

    const max = Math.max(...result.map(hourObj => hourObj.bookingsPerCourse.reduce((a, c) => a + c.bookings, 0)));

    ReactDOM.unstable_batchedUpdates(() => {
      if (max > 0) {
        setMaxBookings(Math.max(max, 25));
      } else {
        setMaxBookings(25);
      }
      setGraphTeeTimeHours(result);
    });
  }

  function getHourLabelText(hour: number) {
    if (hour <= 12) {
      return String(hour);
    } else if (hour === 0 || hour === 24) {
      return "12";
    } else {
      return String(hour % 12);
    }
  }

  function convertYAxisTitle(title: string) {
    const splitHours = title?.split("-");
    const finalTitle = splitHours?.map(hour => getHourLabelText(Number(hour))?.concat(":00"))?.join(" - ");
    return finalTitle;
  }

  //Display y-axis
  function yAxis() {
    const scaleStep = getYAxisScale(maxBookings);
    const yAxisSteps = Math.ceil(maxBookings / scaleStep) + 1;

    return (
      <div className="player-booking-graph-y-axis">
        <div className="player-booking-graph-y-axis-inner">
          {Array.from({ length: yAxisSteps + 1 }).map((_, i) => {
            const val = i * scaleStep;
            return (
              <div
                key={val}
                className="player-booking-graph-y-axis-row"
                style={{ height: singleBarHeight * scaleStep }}
              >
                <div className="player-booking-graph-y-axis-label">{val}</div>
                <div className="player-booking-graph-y-axis-line" />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function getYAxisScale(maxValue: number) {
    if (maxValue >= 100) {
      return 20;
    }
    if (maxValue >= 10) {
      return 5;
    }
    return 5;
  }

  return (
    <div
      className="player-booking-graph-container"
      style={{
        display: (!graphTeeTimeHours || graphTeeTimeHours?.length === 0) && "none",
      }}
    >
      <div className="player-booking-graph">
        {yAxis()}
        <div className="player-booking-graph-plot-area" style={{ minHeight: `${plotAreaHeight}px` }}>
          {graphTeeTimeHours?.map(graphHour => {
            const showStackedToolTip = graphHour?.bookingsPerCourse?.filter(course => course?.bookings > 0)?.length > 1;
            const hideTotalToolTip = graphHour?.bookingsPerCourse?.every(course => course?.bookings === 0);
            return (
              <div key={graphHour.hour} className="player-booking-graph-hour">
                <OverlayTrigger
                  placement="left-end"
                  show={hideTotalToolTip ? false : undefined}
                  overlay={
                    <Tooltip id={`player-volume-bar-${graphHour?.hour}`}>
                      {`${t("Total")}`}: {graphHour?.bookingsPerCourse?.reduce((a, c) => a + c.bookings, 0)}
                    </Tooltip>
                  }
                >
                  <div className="player-booking-graph-bar-stack">
                    {graphHour.bookingsPerCourse.map((courseBooking, index) => {
                      const barHeight = courseBooking.bookings * singleBarHeight;
                      if (barHeight > 0) {
                        if (showStackedToolTip) {
                          return (
                            <OverlayTrigger
                              key={courseBooking.courseId}
                              placement="right"
                              overlay={
                                graphHour?.bookingsPerCourse?.length > 1 ? (
                                  <Tooltip id={`player-volume-bar-${index}`}>
                                    {graphHour?.bookingsPerCourse[index]?.bookings}
                                  </Tooltip>
                                ) : undefined
                              }
                            >
                              <div
                                className="player-booking-graph-bar"
                                style={{
                                  height: `${barHeight}px`,
                                  backgroundColor: DASHBOARD_COLOURS[index % DASHBOARD_COLOURS.length],
                                }}
                              />
                            </OverlayTrigger>
                          );
                        } else {
                          return (
                            <div
                              key={courseBooking.courseId}
                              className="player-booking-graph-bar"
                              style={{
                                height: `${barHeight}px`,
                                backgroundColor: DASHBOARD_COLOURS[index % DASHBOARD_COLOURS.length],
                              }}
                            />
                          );
                        }
                      }
                    })}
                  </div>
                </OverlayTrigger>
                <div className="player-booking-graph-hour-label">{convertYAxisTitle(graphHour.hour)}</div>
                <div className="player-booking-graph-hour-tick" />
              </div>
            );
          })}
        </div>
        {/* X-Axis Title */}
        <div className="player-booking-graph-x-axis-title">{t("Time (per hour)")}</div>
        {/* Y-Axis Title */}
        <div className="player-booking-graph-y-axis-title">{t("No. of players")}</div>
        {/* Selected Courses */}
        {facilityStore?.facility?.courses?.length > 1 ? (
          <div className="player-booking-graph-courses">
            {facilityStore?.facility?.courses
              ?.filter(course => courseIds?.includes(course?.id))
              ?.map((course, index) => {
                return (
                  <p key={course?.id} className="player-booking-graph-course">
                    <span
                      className="player-booking-graph-course-colour"
                      style={{ backgroundColor: DASHBOARD_COLOURS[index % DASHBOARD_COLOURS.length] }}
                    ></span>{" "}
                    {course?.long_name}
                  </p>
                );
              })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
